import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Image, Tag, DollarSign, Info, Eye, AlertTriangle } from 'lucide-react';

interface PublishWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: (workflowData: any) => void;
  workflowData?: any;
}

const categories = [
  { id: 'ai-automation', name: 'AI & Automation', icon: '🤖' },
  { id: 'data-processing', name: 'Data Processing', icon: '📊' },
  { id: 'image-generation', name: 'Image Generation', icon: '🎨' },
  { id: 'text-processing', name: 'Text Processing', icon: '📝' },
  { id: 'productivity', name: 'Productivity', icon: '⚡' },
  { id: 'templates', name: 'Templates', icon: '📋' }
];

export const PublishWorkflowModal= ({
  isOpen,
  onClose,
  onPublish,
  workflowData
}: PublishWorkflowModalProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    tags: [] as string[],
    price: 0,
    isPremium: false,
    screenshots: [] as string[],
    thumbnail: '',
    version: '1.0.0',
    compatibility: ['v1.0+'],
    requirements: [] as string[],
    workflowData: workflowData || null
  });
  
  const [currentTag, setCurrentTag] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = (e: React.KeyEvent) => {
    if (e.key === 'Enter' && currentTag.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(currentTag.trim().toLowerCase())) {
        handleInputChange('tags', [...formData.tags, currentTag.trim().toLowerCase()]);
      }
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    handleInputChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileUpload = (files: FileList | null, type: 'thumbnail' | 'screenshots') => {
    if (!files) return;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        
        if (type === 'thumbnail') {
          handleInputChange('thumbnail', dataUrl);
        } else {
          handleInputChange('screenshots', [...formData.screenshots, dataUrl]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent, type: 'thumbnail' | 'screenshots') => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files, type);
  };

  const validateStep = (stepNumber: number) => {
    switch (stepNumber) {
      case 1:
        return formData.title.trim() && formData.description.trim() && formData.shortDescription.trim();
      case 2:
        return formData.category && formData.tags.length > 0;
      case 3:
        return formData.thumbnail || formData.screenshots.length > 0;
      case 4:
        return true; // Pricing is optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onPublish(formData);
      onClose();
    } catch (error) {
      console.error('Error publishing workflow:', error);
      alert('Failed to publish workflow. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ModalContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="">
            <div>
              <h3 className="text-lg fw-semibold text-dark mb-3">
                Basic Information
              </h3>
              
              <div className="">
                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter workflow title..."
                    className="w-100 px-2 py-2 border border rounded-2 bg-white text-dark"
                  />
                </div>

                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Short Description *
                  </label>
                  <input
                    type="text"
                    value={formData.shortDescription}
                    onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                    placeholder="Brief description for the card (max 120 characters)..."
                    maxLength={120}
                    className="w-100 px-2 py-2 border border rounded-2 bg-white text-dark"
                  />
                  <div className="small text-muted mt-1">
                    {formData.shortDescription.length}/120 characters
                  </div>
                </div>

                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Detailed Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Detailed description of your workflow, what it does, how to use it..."
                    rows={6}
                    className="w-100 px-2 py-2 border border rounded-2 bg-white text-dark"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="">
            <div>
              <h3 className="text-lg fw-semibold text-dark mb-3">
                Categorization & Tags
              </h3>
              
              <div className="">
                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Category *
                  </label>
                  <div className="row row-cols-2 g-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleInputChange('category', category.id)}
                        className={`p-3 border rounded-2 text-start  ${
                          formData.category === category.id
                            ? 'border-blue-500 bg-blue-50  text-blue-700 '
                            : 'border   '
                        }`}
                      >
                        <div className="flex align-items-center gap-2">
                          <span className="text-lg">{category.icon}</span>
                          <span className="fw-medium">{category.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Tags * (Press Enter to add)
                  </label>
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Add tags (e.g., ai, automation, image-processing)..."
                    className="w-100 px-2 py-2 border border rounded-2 bg-white text-dark"
                  />
                  
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.tags.map((tag) => (
                        <span
                          key={tag}
                          className="d-inline-flex align-items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-pill small"
                        >
                          #{tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="rounded-pill p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="">
            <div>
              <h3 className="text-lg fw-semibold text-dark mb-3">
                Visual Assets
              </h3>
              
              <div className="">
                {/* Thumbnail */}
                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Thumbnail Image
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-2 p-3 text-center  ${
                      dragOver
                        ? 'border-blue-500 bg-blue-50 '
                        : 'border '
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'thumbnail')}
                  >
                    {formData.thumbnail ? (
                      <div className="relative">
                        <img
                          src={formData.thumbnail}
                          alt="Thumbnail preview"
                          className="max-h-32 mx-auto rounded-2"
                        />
                        <button
                          onClick={() => handleInputChange('thumbnail', '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-pill"
                        >
                          <X className="" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Image className="w-12 h-12 mx-auto text-muted mb-3" />
                        <p className="text-muted mb-2">
                          Drag & drop a thumbnail image or
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e.target.files, 'thumbnail')}
                          className="d-none"
                          id="thumbnail-upload"
                        />
                        <label
                          htmlFor="thumbnail-upload"
                          className="cursor-pointer text-primary"
                        >
                          browse files
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Screenshots */}
                <div>
                  <label className="d-block small fw-medium text-secondary mb-2">
                    Screenshots (Optional)
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-2 p-3 text-center  ${
                      dragOver
                        ? 'border-blue-500 bg-blue-50 '
                        : 'border '
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'screenshots')}
                  >
                    <Upload className="w-12 h-12 mx-auto text-muted mb-3" />
                    <p className="text-muted mb-2">
                      Add multiple screenshots
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleFileUpload(e.target.files, 'screenshots')}
                      className="d-none"
                      id="screenshots-upload"
                    />
                    <label
                      htmlFor="screenshots-upload"
                      className="cursor-pointer text-primary"
                    >
                      Choose files
                    </label>
                  </div>
                  
                  {formData.screenshots.length > 0 && (
                    <div className="row row-cols-3 g-2 mt-3">
                      {formData.screenshots.map((screenshot, index) => (
                        <div key={index} className="relative">
                          <img
                            src={screenshot}
                            alt={`Screenshot ${index + 1}`}
                            className="w-100 h-20 object-cover rounded-2"
                          />
                          <button
                            onClick={() => {
                              const newScreenshots = formData.screenshots.filter((_, i) => i !== index);
                              handleInputChange('screenshots', newScreenshots);
                            }}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-pill"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="">
            <div>
              <h3 className="text-lg fw-semibold text-dark mb-3">
                Pricing & Publishing Options
              </h3>
              
              <div className="">
                <div className="bg-yellow-50 border border-yellow-200 rounded-2 p-3">
                  <div className="flex align-items-start gap-2">
                    <Info className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="fw-medium text-warning">
                        Monetization Information
                      </h4>
                      <p className="small text-yellow-700 mt-1">
                        • Free items are available immediately to all users
                        • Premium items require purchase (70% revenue share to creator)
                        • All items go through moderation before publication
                      </p>
                    </div>
                  </div>
                </div>

                <div className="">
                  <div>
                    <label className="flex align-items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pricing"
                        checked={!formData.isPremium}
                        onChange={() => {
                          handleInputChange('isPremium', false);
                          handleInputChange('price', 0);
                        }}
                        className="text-primary"
                      />
                      <div>
                        <div className="fw-medium text-dark">
                          🆓 Free
                        </div>
                        <div className="small text-muted">
                          Available to all users immediately
                        </div>
                      </div>
                    </label>
                  </div>

                  <div>
                    <label className="flex align-items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pricing"
                        checked={formData.isPremium}
                        onChange={() => handleInputChange('isPremium', true)}
                        className="text-primary mt-1"
                      />
                      <div className="flex-grow-1">
                        <div className="fw-medium text-dark mb-2">
                          💎 Premium
                        </div>
                        {formData.isPremium && (
                          <div className="">
                            <div className="flex align-items-center gap-2">
                              <DollarSign className="text-muted" />
                              <input
                                type="number"
                                min="0.99"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                placeholder="Price in USD"
                                className="flex-grow-1 px-2 py-2 border border rounded-2 bg-white text-dark"
                              />
                            </div>
                            <div className="small text-muted bg-light p-3 rounded-2">
                              <div className="fw-medium mb-1">Revenue Split:</div>
                              <div>• Creator: ${(formData.price * 0.7).toFixed(2)} (70%)</div>
                              <div>• Platform: ${(formData.price * 0.3).toFixed(2)} (30%)</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="">
            <div>
              <h3 className="text-lg fw-semibold text-dark mb-3">
                Review & Publish
              </h3>
              
              <div className="bg-light rounded-2 p-3">
                <div className="row row-cols-2 g-2 small">
                  <div>
                    <span className="fw-medium text-secondary">Title:</span>
                    <p className="text-dark mt-1">{formData.title}</p>
                  </div>
                  <div>
                    <span className="fw-medium text-secondary">Category:</span>
                    <p className="text-dark mt-1">
                      {categories.find(c => c.id === formData.category)?.name}
                    </p>
                  </div>
                  <div>
                    <span className="fw-medium text-secondary">Price:</span>
                    <p className="text-dark mt-1">
                      {formData.price === 0 ? 'Free' : `$${formData.price.toFixed(2)}`}
                    </p>
                  </div>
                  <div>
                    <span className="fw-medium text-secondary">Tags:</span>
                    <p className="text-dark mt-1">
                      {formData.tags.join(', ')}
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-top border">
                  <div className="flex align-items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-2">
                    <AlertTriangle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="fw-medium text-blue-800">
                        Before Publishing
                      </h4>
                      <ul className="small text-blue-700 mt-1">
                        <li>• Your workflow will be reviewed for quality and compliance</li>
                        <li>• Review typically takes 24-48 hours</li>
                        <li>• You'll receive an email notification when approved</li>
                        <li>• Make sure your workflow is properly tested and documented</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3 bg-dark bg-opacity-50"
        style={{ zIndex: 1050 }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-3 shadow-lg w-100 overflow-hidden" style={{ maxHeight: '90vh', maxWidth: '42rem' }}
        >
          {/* Header */}
          <div className="flex align-items-center justify-content-between p-3 border-bottom border">
            <div>
              <h2 className="text-xl fw-semibold text-dark">
                Publish to Marketplace
              </h2>
              <p className="small text-muted mt-1">
                Step {step} of 5
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-2 pt-4">
            <div className="w-100 bg-light rounded-pill">
              <div
                className="bg-primary rounded-pill"
                style={{ width: `${(step / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-3 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <ModalContent />
          </div>

          {/* Footer */}
          <div className="flex align-items-center justify-content-between p-3 border-top border">
            <button
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              className="px-2 py-2 text-muted"
            >
              {step > 1 ? 'Back' : 'Cancel'}
            </button>

            <div className="flex gap-2">
              {step < 5 ? (
                <button
                  onClick={handleNext}
                  disabled={!validateStep(step)}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="btn btn-success d-flex align-items-center gap-2"
                >
                  {isSubmitting ? 'Publishing...' : 'Publish Workflow'}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};